/**
 * Tags carried by a visit's file/image field value.
 *
 * The API returns them inside the value of the file question itself — `custom_tags` (user-created)
 * and `form_tags` (bound to the form's file question) — so there is no fixed attribute to look at:
 * `store_image` is just what one form calls its file question. Any value shaped like
 * `{ file_path, custom_tags: [{ id, name }] }` is a tagged file, whatever the question is named.
 *
 * @example getFileTags({ file_name: 'active.png', custom_tags: [{ id: 208, name: 'тест' }] })
 * return [{ id: 208, name: 'тест', formLinked: false }]
 *
 * @param {object|array|string} value raw attribute value of a file question
 *
 * @return {array} [{ id, name, formLinked }], empty when the value carries no tags
 */

// Tolerates full tag objects (`[{ id, name }]`) and bare names/ids, the same way the media galleries do.
const normalizeTags = (tags, formLinked) =>
  (tags || [])
    .map((tag) => (tag && typeof tag === 'object' ? tag : { id: tag, name: tag }))
    .filter((tag) => tag.name)
    .map(({ id, name }) => ({ id, name, formLinked }));

export const getFileTags = (value) => {
  const file = Array.isArray(value) ? value[0] : value;
  if (!file || typeof file !== 'object') return [];

  const tags = [...normalizeTags(file.form_tags, true), ...normalizeTags(file.custom_tags, false)];

  // A tag can come back in both arrays; keep the first (form-linked) copy so it isn't shown twice.
  const seen = new Set();
  return tags.filter(({ id, name }) => {
    const key = id ?? name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
